import * as React from 'react';
import { useEffect } from 'react';

/** Redux Packages. */
import { useSelector } from 'react-redux';

/** Deep comparison and empty value checking. */
import isEqual from 'lodash/isEqual';

/** Material UI Packages. */
import { randomId } from '@mui/x-data-grid-generator';

/** Internal Components. */
import { settingOption } from 'shared/config/constantsData';

export default function ApplySettingToggleHandler(props) {
    /** Get props from ApplySetting.js. */
    const { presetUIState, selectedRow, presetNameInputRef, applySettingData, dispatch, setDirtyFlags } = props;

    /** Destructure state. */
    const { renderValue, presetName, mode, initEditValue } = presetUIState;

    /** Reducer state for managing all preset notes of the currently selected trade partner. */
    const presetNoteForm = useSelector((state) => state.presetNoteForm);

    /** Function for creating a new preset when switching to edit mode. */
    const generateEmptyPreset = () => {
        const presetId = randomId();
        const presetDetailId = randomId();
        const newPreset = {
            id: presetId,
            name: '',
            il_trade_partner_id: selectedRow?.id,
            note: '',
            isNew: true,
        };
        const detailIdObj = {
            id: presetDetailId,
            il_preset_id: presetId,
        };
        const presetTypes = {
            il_preset_ocean_import: 0,
            il_preset_ocean_export: 1,
            il_preset_air_import: 2,
            il_preset_air_export: 3,
            il_preset_ftl_ltl: 4,
        };
        // This section creates the il_preset object.
        // For each category in presetTypes, it generates the corresponding preset options, initializing all values to false and adding them to newPreset.
        // All presetTypes share the same set of detailIdObj keys.
        // Example of Object.entries(presetTypes): [['il_preset_ocean_import', 0], ['il_preset_ocean_export', 1], ['il_preset_air_import', 2], ...]
        // Each key looks like 'il_preset_ocean_import', 'il_preset_ocean_export', etc.
        // In settingOption, each key includes an index that indicates whether the given shipment type uses that option.
        // If the index includes the current shipment category, the value is initialized as false.
        for (const [key, index] of Object.entries(presetTypes)) {
            newPreset[key] = {
                ...detailIdObj,
                ...Object.fromEntries(
                    settingOption
                        .filter((option) => option.index.includes(index))
                        .map((option) => [option.value, false])
                ),
            };
        }

        return newPreset;
    };

    /** Function to warn the user when trying to change modes while input is not empty.  */
    const createModeWarning = () => {
        const isUserInput = () => {
            const valueList = Object.values(renderValue);
            const resultList = valueList.map((value) => {
                if (typeof value !== 'object') return false;
                const presetValues = Object.values(value);
                return presetValues.includes(true);
            });
            return resultList.includes(true);
        };

        if (mode === 'create' && (presetName.trim() !== '' || isUserInput())) {
            return !window.confirm('Your unsaved changes will be lost. Are you sure?');
        } else return false;
    };

    /** Function to warn the user when trying to change modes while input is not empty. */
    const editModeWarning = () => {
        if (mode === 'edit') {
            const isEditModified = () => {
                return !isEqual(initEditValue, renderValue) || initEditValue.name !== presetName;
            };

            return isEditModified() ? !window.confirm('Your unsaved changes will be lost. Are you sure?') : false;
        }
    };

    /** Handler to active note mode.
     *  When the note is first loaded, it always loads the alert note content for the ocean import preset first, as it is the default selected option.
     * */
    const handleNoteEditActive = (newRenderValue) => {
        const loadFirstNote = () => {
            const foundPresetNote = presetNoteForm.find(
                (note) =>
                    note.il_preset_id === newRenderValue.id &&
                    note.shipment_type === 0 &&
                    note.attribute === 'pre_alert'
            );
            if (foundPresetNote) return foundPresetNote.content;
            else return '';
        };

        // Make exception if preset is empty.
        if (Object.keys(newRenderValue).length === 0) {
            alert('No preset is selected.');
        } else {
            // Check if there is an OIM and pre-alert note, and apply its value. If not, apply an empty note value.
            const firstNote = presetNoteForm.find(
                (note) =>
                    note.il_preset_id === newRenderValue.id &&
                    note.shipment_type === 0 &&
                    note.attribute === 'pre_alert'
            );
            dispatch({
                type: 'SET_MULTIPLE',
                payload: {
                    selectedNote: {
                        index: 0,
                        progress: { value: 'pre_alert', label: 'Pre Alert' },
                        content: firstNote?.value ?? loadFirstNote(),
                    },
                    mode: 'note',
                },
            });
        }
    };

    /** Handler for the create mode toggle button. Before switching, it alerts the user if there is unsaved data, then switches to create mode. */
    const handleCreate = () => {
        if (editModeWarning()) return;

        const newPreset = generateEmptyPreset();

        setDirtyFlags((prev) => ({ ...prev, isPresetDirty: true }));
        presetNameInputRef.current?.focus();

        dispatch({
            type: 'SET_MULTIPLE',
            payload: {
                presetName: '',
                renderValue: newPreset,
                mode: 'create',
            },
        });
    };

    /** Handler for edit mode toggle button. Before switching, it alerts user if there's non-saved data, and then modifies into edit mode state. */
    const handleEdit = () => {
        if (createModeWarning()) return;

        if (renderValue?.isNew) {
            dispatch({
                type: 'SET_MULTIPLE',
                payload: {
                    presetName: applySettingData[0].name,
                    renderValue: applySettingData[0] ?? {},
                    initEditValue: applySettingData[0],
                    mode: 'edit',
                },
            });
        } else {
            dispatch({
                type: 'SET_MULTIPLE',
                payload: {
                    presetName: renderValue.name,
                    initEditValue: renderValue,
                    mode: 'edit',
                },
            });
        }
        setDirtyFlags((prev) => ({ ...prev, isPresetDirty: true }));
    };

    /** Handler for note mode toggle button. Before switching, it alerts user if there's non-saved data, and then modifies into note mode state. */
    const handleNote = () => {
        if (createModeWarning() || editModeWarning()) return;

        if (renderValue?.isNew) {
            dispatch({ type: 'SET_MULTIPLE', payload: { renderValue: applySettingData[0] ?? {} } });
        }
        setDirtyFlags((prev) => ({ ...prev, isPresetDirty: false }));
        handleNoteEditActive(renderValue);

        dispatch({ type: 'SET_MULTIPLE', payload: { presetName: '', mode: 'note' } });
    };

    /** Handler to update toggleButtonGroup's each button. */
    const handleModeChange = (event, newMode) => {
        switch (newMode) {
            case 'create':
                handleCreate();
                return;
            case 'edit':
                handleEdit();
                return;
            case 'note':
                handleNote();
                return;
            default:
                dispatch({
                    type: 'SET_MULTIPLE',
                    payload: { presetName: '', renderValue: applySettingData[0] ?? {}, mode: 'view' },
                });
                setDirtyFlags((prev) => ({ ...prev, isPresetDirty: false }));
                return;
        }
    };

    /**
     * Initialize the preset list.
     * - If presets exist, sets the first preset as the rendering value.
     * - If no presets are available, initializes with empty values.
     */
    useEffect(() => {
        if (Object.keys(renderValue).length === 0) {
            if (applySettingData && applySettingData.length > 0) {
                dispatch({ type: 'SET_MULTIPLE', payload: { renderValue: applySettingData[0] } });
            } else {
                dispatch({ type: 'SET_MULTIPLE', payload: { renderValue: {} } });
            }
        }
    }, [applySettingData]);

    return { handleModeChange, handleNoteEditActive };
}
